import type { VanillaLinkProps } from '../Link/index.ts';
import clsx from 'clsx';
import { createContext, use, useMemo } from 'react';

type ListVariant = 'default' | 'distinct' | 'wrapped';

interface ListContextType {
  bleed?: boolean | 'mobile';
  variant: ListVariant;
}

const ListContext = createContext<ListContextType>({} as ListContextType);

export interface ListProps {
  bleed?: boolean | 'mobile';
  children?: React.ReactNode;
  variant?: ListVariant;
}

export function List({ bleed = false, children, variant = 'default' }: ListProps) {
  return (
    <ListContext value={useMemo(() => ({ bleed, variant }), [bleed, variant])}>
      <div
        className={clsx(
          variant === 'wrapped' && [
            'overflow-hidden border-gray-200 bg-white',
            !bleed && 'rounded-md border',
            bleed === 'mobile' && 'border-t border-b sm:rounded-md sm:border'
          ],
          (bleed === true || bleed === 'mobile') && '-mx-(--gutter-x)',
          bleed === 'mobile' && 'sm:mx-0'
        )}
      >
        <ul
          className={clsx(
            variant === 'distinct' ? 'flex flex-col gap-y-3' : 'divide-y divide-gray-200'
          )}
        >
          {children}
        </ul>
      </div>
    </ListContext>
  );
}

export type ListItemProps = React.PropsWithChildren<{
  onClick?: () => void;
  renderLink?: (props: VanillaLinkProps) => React.ReactNode;
}>;

export function ListItem({ children, onClick, renderLink }: ListItemProps) {
  const { bleed, variant } = use(ListContext);

  const styles = clsx(
    'relative py-5',

    variant === 'default' && [
      bleed && 'px-(--gutter-x)',
      (!bleed || bleed === 'mobile') && 'sm:px-0'
    ],

    (variant === 'distinct' || variant === 'wrapped') && [
      bleed ? 'px-(--gutter-x)' : 'px-6',
      (!bleed || bleed === 'mobile') && 'sm:px-6'
    ],

    variant === 'distinct' && [
      'overflow-hidden border-gray-200 bg-white',
      bleed && 'border-t border-b',
      !bleed && 'rounded-md border',
      (!bleed || bleed === 'mobile') && 'sm:rounded-md sm:border'
    ],

    (onClick || renderLink) &&
      'before:pointer-events-none before:absolute before:inset-0 hover:before:bg-black/2.5'
  );

  if (renderLink) {
    return (
      <li>
        {renderLink({
          children: <div className={clsx(styles)}>{children}</div>,
          className: 'block',
          onClick,
          plain: true
        })}
      </li>
    );
  }

  return (
    <li className={clsx(styles)} onClick={onClick} role={onClick ? 'button' : undefined}>
      {children}
    </li>
  );
}
